import React, { useCallback } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { Subscribe } from 'unstated';

import ShowMoreIcon from '@atlaskit/icon/core/show-more-horizontal';
import AutomationIcon from '@atlaskit/icon/core/automation';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import ShapesIcon from '@atlaskit/icon/core/shapes';
import PersonIcon from '@atlaskit/icon/core/person';
import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';
import AppsIcon from '@atlaskit/icon/core/apps';
import ArchiveBoxIcon from '@atlaskit/icon/core/archive-box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import CopyIcon from '@atlaskit/icon/core/copy';
import ChangesIcon from '@atlaskit/icon/core/changes';
import Toggle from '@atlaskit/toggle/toggle';
import UploadIcon from '@atlaskit/icon/core/upload';
import FolderOpenIcon from '@atlaskit/icon/core/folder-open';
import QuotationMarkIcon from '@atlaskit/icon/core/quotation-mark';
import DeleteIcon from '@atlaskit/icon/core/delete';

import { fg } from '@confluence/feature-gating';
import { ANALYTICS_ADDON_ACTIONS_MENU_ID } from '@confluence/web-item/entry-points/webItemIDs.ts';
import { CONNECT_APP_PREFIX_KEY } from '@confluence/web-item/entry-points/webItemKeys.ts';
import { LazyForgeContentTools } from '@confluence/content-tools/entry-points/LazyForgeContentTools.ts';
import { AUTOMATION_MANUAL_TRIGGERS } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { PageQuickActionAutomationMenu } from '@confluence/automation-menu/entry-points/PageQuickActionAutomationMenu.ts';
import { ContentOwnerPopup } from '@confluence/content-ownership/entry-points/ContentOwnerPopupComponent.ts';
import { ViewChangesDialogLoader } from '@confluence/view-changes-dialog';
import { DialogsStateContainer } from '@confluence/dialogs/entry-points/DialogsStateContainer.ts';
import {
	ConvertToFolderItem,
	LazyContentToolsItem,
} from '@confluence/content-tools/entry-points/ContentToolsButton.ts';
import { PublishPageAsBlogDialog } from '@confluence/publish-page-as-blog/entry-points/PublishPageAsBlogDialog.ts';
import { PublishPageAsBlogSources } from '@confluence/publish-page-as-blog/entry-points/PublishPageAsBlogSourcesEnum.ts';
import { ShortcutVisualizer } from '@confluence/shortcuts/entry-points/ShortcutVisualizer.ts';
import { expValEquals } from '@confluence/feature-experiments';

import {
	ACTION_EXPORT_PDF,
	ACTION_EXPORT_WORD,
	ACTION_EXPORT_MARKDOWN,
	ACTION_ARCHIVE_CONTENT,
	ACTION_REMOVE_CONTENT,
	ACTION_CONVERT_TO_BLOG,
	ACTION_CONVERT_TO_FOLDER,
	SWITCH_TO_LIVE_EDIT_WEB_ITEM,
	ACTION_COPY_CONTENT,
	ACTION_EXPORT_CONTENT,
	ACTION_CONVERT_CONTENT,
	ACTION_CONVERT_TO_CONTENT,
	ACTION_VIEW_STORAGE_FORMAT,
	ACTION_VIEW_SOURCE,
} from '../moreActionsConstants.ts';

import { ConvertToLivePageItem } from './ConvertToLivePageMenuItem.tsx';
import { ExportMarkdownMenuItem } from './ExportMarkdownMenuItem.tsx';
import {
	ExpandableNestedMenuItem,
	type ExpandableNestedMenuItemItemType,
} from './ExpandableNestedMenuItem.tsx';
export const ACTION_APPS = 'action-apps';

export const i18n: {
	export: {
		id: string;
		description: string;
		defaultMessage: string;
	};
	convert: {
		id: string;
		description: string;
		defaultMessage: string;
	};
	duplicate: {
		id: string;
		description: string;
		defaultMessage: string;
	};
	makeACopy: {
		id: string;
		description: string;
		defaultMessage: string;
	};
	duplicateDatabase: {
		id: string;
		description: string;
		defaultMessage: string;
	};
	makeACopyOfDatabase: {
		id: string;
		description: string;
		defaultMessage: string;
	};
	archive: {
		id: string;
		description: string;
		defaultMessage: string;
	};
	deleteDraftLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	deleteLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	archiveAndDeleteLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	viewChangesTitle: {
		id: string;
		description: string;
		defaultMessage: string;
	};
	viewChangesLabel: {
		id: string;
		description: string;
		defaultMessage: string;
	};
	automationsModalTitle: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	automationsLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	advancedLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	pageAppsLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	export: {
		id: 'content-types-header.ellipsis.open.ellipsis.export',
		description:
			'The text that is displayed in the menu dropdown that groups Export to PDF and Export to Word',
		defaultMessage: 'Export',
	},
	convert: {
		id: 'content-types-header.ellipsis.open.ellipsis.convert',
		description:
			'The text that is displayed in the menu dropdown that groups convert to other content type actions',
		defaultMessage: 'Convert',
	},
	duplicate: {
		id: 'content-types-header.ellipsis.open.ellipsis.duplicate',
		description:
			'The text that is displayed in the menu dropdown that groups Duplicate Database and Copy structure actions',
		defaultMessage: 'Duplicate',
	},
	makeACopy: {
		id: 'content-types-header.ellipsis.open.ellipsis.makeACopy',
		description:
			'The text that is displayed in the menu dropdown that groups Make a copy of Database and Copy structure actions',
		defaultMessage: 'Make a copy',
	},
	duplicateDatabase: {
		id: 'content-types-header.ellipsis.open.ellipsis.duplicate.database',
		description:
			'The text is shown as a drop-down menu item label in the more actions menu when the user can duplicate a database content type.',
		defaultMessage: 'Duplicate database',
	},
	makeACopyOfDatabase: {
		id: 'content-types-header.ellipsis.open.ellipsis.makeACopy.database',
		description: 'Label for making a copy of database action',
		defaultMessage: 'Make a copy of database',
	},
	archive: {
		id: 'content-types-header.ellipsis.open.ellipsis.archive',
		description: 'The text that is displayed in the menu dropdown that groups Archive and Delete',
		defaultMessage: 'Archive',
	},
	deleteDraftLabel: {
		id: 'content-types-header.content-types.more-actions.delete-draft',
		defaultMessage: 'Delete draft',
		description:
			'The text is shown as a drop-down menu item label in the more actions menu when the user can delete a draft version of the content.',
	},
	deleteLabel: {
		id: 'content-types-header.content-types.more-actions.delete',
		defaultMessage: 'Delete',
		description: 'Label for the delete action button in the more actions menu',
	},
	archiveAndDeleteLabel: {
		id: 'content-types-header.content-types.more-actions.archive-and-delete',
		defaultMessage: 'Archive and delete',
		description:
			'The text is shown as a drop-down menu item label in the more actions menu when the user can archive and delete the content in a single action.',
	},
	viewChangesTitle: {
		id: 'content-types-header.content-types.view.changes.title',
		description: 'heading of view changes dialog in a live page',
		defaultMessage: 'Latest changes',
	},
	viewChangesLabel: {
		id: 'content-types-header.content-types.view.changes.label',
		description: 'The label of View changes button inside ellipsis dropdown of the editor',
		defaultMessage: 'View changes',
	},
	automationsModalTitle: {
		id: 'content-types-header.automations.title',
		defaultMessage: 'Page automations',
		description: 'The title of the page automation modal header.',
	},
	automationsLabel: {
		id: 'content-types-header.automations.label',
		defaultMessage: 'Automations',
		description: 'The label of the Automations button inside the ellipsis dropdown',
	},
	advancedLabel: {
		id: 'content-types-header.advanced.label',
		defaultMessage: 'Advanced details',
		description: 'The label of the Advanced details button inside the ellipsis dropdown',
	},
	pageAppsLabel: {
		id: 'content-types-header.ellipsis.open.ellipsis.page.apps',
		defaultMessage: 'Apps',
		description:
			'The text in dropdown menu that displays all the apps that are available for the page',
	},
});

export enum NestedMenuTypes {
	Export = 'export',
	Convert = 'convert',
	ConvertTo = 'convert-to',
	Archive = 'archive',
	Advanced = 'advanced',
	Attachments = 'attachments',
	Apps = 'apps',
	ContentOwnership = 'content-ownership',
	MoreSupport = 'more-support',
	CopyDatabase = 'copy-database',
	Help = 'help',
	Watch = 'watch',
	TemplatesAndImport = 'templates-and-import',
}

type CustomActionProps = {
	item: {
		id: string;
		text: string;
		shortcutText?: string;
		isDisabled?: boolean;
		showToggle?: {
			isDisabled: boolean;
			isChecked?: boolean;
		};
		onClick?: () => void;
		url?: string;
		target?: string;
	};
	icon?: React.ReactNode;
	testId?: string;
};

const ConvertMenuItemComponent = ({
	webItemsMap,
	isNestedMenuOpen,
	handleClick,
	spaceKey,
	contentId,
	dialogs,
	handleNestedMenuItemClose,
	contentMode,
	flags,
	spaceId,
	closeParentMenu,
	isConvertToFolderHighlighted,
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const convertItems = [
		SWITCH_TO_LIVE_EDIT_WEB_ITEM,
		ACTION_CONVERT_TO_FOLDER,
		ACTION_CONVERT_TO_BLOG,
	]
		.map((id) => {
			const item = webItemsMap[id];
			if (id === ACTION_CONVERT_TO_FOLDER && item) {
				item.url = '';
				item.urlWithoutContextPath = '';
				return {
					...item,
					customComponent: () => (
						<ConvertToFolderItem
							key={item.completeKey}
							sectionItem={item}
							contentId={contentId}
							spaceKey={spaceKey}
							dialogs={dialogs}
							shouldHighlight={isConvertToFolderHighlighted}
							elemBefore={<FolderOpenIcon label="" />}
							onClick={closeParentMenu}
						/>
					),
				};
			} else if (id === ACTION_CONVERT_TO_BLOG && item) {
				item.url = '';
				item.urlWithoutContextPath = '';

				const makePublishPageAsBlogDialogOpener = () => {
					closeParentMenu();
					createAnalyticsEvent({
						type: 'sendUIEvent',
						data: {
							action: 'clicked',
							actionSubject: 'button',
							actionSubjectId: 'publishPageAsBlog',
							source: 'contentTypesHeader',
						},
					}).fire();
					dialogs.showDialog(PublishPageAsBlogDialog, {
						contentId,
						spaceKey,
						source: PublishPageAsBlogSources.ViewPage,
					});
				};

				return {
					...item,
					customComponent: () => {
						return (
							<LazyContentToolsItem
								key={item.completeKey}
								{...item}
								onClick={makePublishPageAsBlogDialogOpener}
								elemBefore={<QuotationMarkIcon label="" />}
							/>
						);
					},
				};
			} else if (id === SWITCH_TO_LIVE_EDIT_WEB_ITEM && item) {
				return {
					...item,
					customComponent: () => (
						<ConvertToLivePageItem
							item={item}
							contentMode={contentMode}
							spaceId={spaceId}
							flags={flags}
							spaceKey={spaceKey}
							contentId={contentId}
							closeParentMenu={closeParentMenu}
						/>
					),
				};
			} else {
				return null;
			}
		})
		.filter((item) => item && item.id);

	return (
		<ExpandableNestedMenuItem
			label={<FormattedMessage {...i18n.convert} />}
			items={convertItems}
			isNestedMenuOpen={isNestedMenuOpen}
			onClick={handleClick}
			iconBefore={<ChangesIcon label="" />}
			key={ACTION_CONVERT_CONTENT}
			onMenuClose={handleNestedMenuItemClose}
		/>
	);
};

export const ConvertMenuItem = ({
	webItemsMap,
	nestedPopup,
	handleNestedMenuItemClick,
	spaceKey,
	contentId,
	handleNestedMenuItemClose,
	flags,
	spaceId,
	contentMode,
	closeParentMenu,
	isConvertToFolderHighlighted,
}: {
	webItemsMap: any;
	nestedPopup: any;
	handleNestedMenuItemClick: any;
	spaceKey: any;
	contentId: any;
	handleNestedMenuItemClose: any;
	flags: any;
	spaceId: any;
	contentMode: any;
	closeParentMenu: any;
	isConvertToFolderHighlighted: any;
}): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const handleConvertMenuItemClick = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'overflowExportAndConvert',
				source: 'overflowMenu',
			},
		}).fire();
		handleNestedMenuItemClick(NestedMenuTypes.Convert);
	};

	return (
		<Subscribe to={[DialogsStateContainer]}>
			{(dialogs) => (
				<ConvertMenuItemComponent
					webItemsMap={webItemsMap}
					dialogs={dialogs}
					isNestedMenuOpen={nestedPopup === NestedMenuTypes.Convert}
					handleClick={handleConvertMenuItemClick}
					contentId={contentId}
					spaceKey={spaceKey}
					flags={flags}
					spaceId={spaceId}
					handleNestedMenuItemClose={handleNestedMenuItemClose}
					contentMode={contentMode}
					closeParentMenu={closeParentMenu}
					isConvertToFolderHighlighted={isConvertToFolderHighlighted}
				/>
			)}
		</Subscribe>
	);
};

// Groups the live-doc/markdown conversion actions (convert to page / markdown /
// rich text) under a single "Convert to" submenu. The eligible sub-items are
// built by the caller and passed in as `items` so the conversion handlers can
// stay colocated with the rest of the More Actions state.
export const ConvertToMenuItem = ({
	items,
	nestedPopup,
	handleNestedMenuItemClick,
	handleNestedMenuItemClose,
}: {
	items: ExpandableNestedMenuItemItemType[];
	nestedPopup: NestedMenuTypes | undefined;
	handleNestedMenuItemClick: (type: NestedMenuTypes) => void;
	handleNestedMenuItemClose: () => void;
}): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleConvertToMenuItemClick = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'overflowConvertTo',
				source: 'overflowMenu',
			},
		}).fire();
		handleNestedMenuItemClick(NestedMenuTypes.ConvertTo);
	};

	return (
		<ExpandableNestedMenuItem
			label={<FormattedMessage {...i18n.convert} />}
			items={items}
			iconBefore={<ChangesIcon label="" />}
			isNestedMenuOpen={nestedPopup === NestedMenuTypes.ConvertTo}
			onClick={handleConvertToMenuItemClick}
			key={ACTION_CONVERT_TO_CONTENT}
			onMenuClose={handleNestedMenuItemClose}
		/>
	);
};

// Content types that support Export to Markdown. The underlying
// `GET /wiki/api/v2/pages/{id}?body-format=markdown` BE endpoint only knows how
// to convert page-like storage XHTML into Markdown, so we restrict the menu item
// to classic pages, live docs (also contentType === 'page' with contentMode === 'live'),
// and blogposts. Other content types (whiteboards, databases, slides, embeds, folders)
// would either 404 or return non-meaningful Markdown.
export const EXPORT_MARKDOWN_SUPPORTED_CONTENT_TYPES: string[] = ['page', 'blogpost'];

export const isExportMarkdownSupportedContentType = (
	contentType: string | null | undefined,
): boolean => !!contentType && EXPORT_MARKDOWN_SUPPORTED_CONTENT_TYPES.includes(contentType);

export const ExportMenuItem = ({
	webItemsMap,
	handleNestedMenuItemClick,
	nestedPopup,
	handleNestedMenuItemClose,
	closeParentMenu,
	contentId,
	contentType,
	contentMode,
	pageTitle,
	contentStatus,
}: {
	webItemsMap: Record<string, any>;
	handleNestedMenuItemClick: (type: NestedMenuTypes) => void;
	nestedPopup: NestedMenuTypes | undefined;
	handleNestedMenuItemClose: () => void;
	closeParentMenu: () => void;
	contentId?: string;
	contentType?: string;
	contentMode?: string;
	pageTitle?: string;
	contentStatus?: string;
}): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const exportItems: ExpandableNestedMenuItemItemType[] = [ACTION_EXPORT_WORD, ACTION_EXPORT_PDF]
		.map((id) => {
			const item = webItemsMap[id];
			return {
				...item,
				closeParentMenu,
			};
		})
		.filter((item) => item.id);

	if (
		!!contentId &&
		isExportMarkdownSupportedContentType(contentType) &&
		expValEquals('confluence_export_markdown', 'isEnabled', true)
	) {
		exportItems.push({
			id: ACTION_EXPORT_MARKDOWN,
			label: '',
			url: '',
			closeParentMenu,
			customComponent: () => (
				<ExportMarkdownMenuItem
					key={ACTION_EXPORT_MARKDOWN}
					contentId={contentId}
					contentType={contentType}
					contentMode={contentMode}
					pageTitle={pageTitle ?? ''}
					contentStatus={contentStatus}
					closeParentMenu={closeParentMenu}
				/>
			),
		});
	}

	const handleExportMenuItemClick = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'overflowExport',
				source: 'overflowMenu',
			},
		}).fire();
		handleNestedMenuItemClick(NestedMenuTypes.Export);
	};

	return (
		<ExpandableNestedMenuItem
			label={<FormattedMessage {...i18n.export} />}
			items={exportItems}
			iconBefore={<UploadIcon label="" />}
			isNestedMenuOpen={nestedPopup === NestedMenuTypes.Export}
			onClick={handleExportMenuItemClick}
			key={ACTION_EXPORT_CONTENT}
			onMenuClose={handleNestedMenuItemClose}
		/>
	);
};

export const DeleteMenuItem = ({
	onDeleteClick,
	contentMode,
	createAnalyticsEvent,
}: {
	onDeleteClick: (dialogs: any) => void;
	contentMode?: string;
	createAnalyticsEvent: CreateUIAnalyticsEvent;
}): React.JSX.Element => {
	const label = contentMode === 'draft' ? i18n.deleteDraftLabel : i18n.deleteLabel;

	return (
		<Subscribe to={[DialogsStateContainer]} key={`container-${ACTION_REMOVE_CONTENT}`}>
			{(dialogs: DialogsStateContainer) => (
				<DropdownItem
					onClick={() => {
						createAnalyticsEvent({
							type: 'sendUIEvent',
							data: {
								action: 'clicked',
								actionSubject: 'button',
								actionSubjectId: 'overflowDelete',
								source: 'overflowMenu',
							},
						}).fire();
						onDeleteClick(dialogs);
					}}
					elemBefore={<DeleteIcon label="" />}
				>
					<FormattedMessage {...label} />
				</DropdownItem>
			)}
		</Subscribe>
	);
};

export const ArchiveMenuItem = ({ onArchiveClick }: { onArchiveClick: any }): React.JSX.Element => {
	return (
		<Subscribe to={[DialogsStateContainer]} key={`container-${ACTION_ARCHIVE_CONTENT}`}>
			{(dialogs: DialogsStateContainer) => (
				<DropdownItem
					onClick={() => {
						onArchiveClick(dialogs);
					}}
					elemBefore={<ArchiveBoxIcon label="" />}
				>
					<FormattedMessage {...i18n.archive} />
				</DropdownItem>
			)}
		</Subscribe>
	);
};

export const ArchiveDeleteMenuItem = ({
	webItems,
	isNestedMenuOpen,
	handleClick,
	onArchiveClick,
	onDeleteClick,
	contentMode,
	createAnalyticsEvent,
	handleNestedMenuItemClose,
}: {
	webItems: any;
	isNestedMenuOpen: any;
	handleClick: any;
	onArchiveClick: any;
	onDeleteClick: any;
	contentMode: any;
	createAnalyticsEvent: any;
	handleNestedMenuItemClose: any;
}): React.JSX.Element => {
	const archiveItems = webItems
		.filter((item) => item.id === ACTION_ARCHIVE_CONTENT || item.id === ACTION_REMOVE_CONTENT)
		.map((item) => {
			if (item.id === ACTION_ARCHIVE_CONTENT) {
				return {
					...item,
					customComponent: () => <ArchiveMenuItem onArchiveClick={onArchiveClick} />,
				};
			} else if (item.id === ACTION_REMOVE_CONTENT) {
				return {
					...item,
					customComponent: () => (
						<DeleteMenuItem
							onDeleteClick={onDeleteClick}
							contentMode={contentMode}
							createAnalyticsEvent={createAnalyticsEvent}
						/>
					),
				};
			}
			return item;
		});

	return (
		<ExpandableNestedMenuItem
			label={<FormattedMessage {...i18n.archiveAndDeleteLabel} />}
			items={archiveItems}
			isNestedMenuOpen={isNestedMenuOpen}
			onClick={handleClick}
			iconBefore={<DeleteIcon label="" />}
			onMenuClose={handleNestedMenuItemClose}
		/>
	);
};

export const ShortcutKeysVisual = ({
	shortcutKeys,
	id,
}: {
	shortcutKeys: string[];
	id: string;
}): React.JSX.Element => (
	<Inline>
		{shortcutKeys.map((key) => (
			<ShortcutVisualizer key={`${id}-shortcut-${key}`} shortcut={key} />
		))}
	</Inline>
);

export const CustomAction = ({ item, icon, testId }: CustomActionProps): React.JSX.Element => {
	const { id, url, target, onClick, shortcutText, text, showToggle, isDisabled } = item;

	const ElementAfter = () => {
		if (shortcutText) {
			return <ShortcutKeysVisual shortcutKeys={shortcutText.split('+')} id={id} />;
		} else if (showToggle) {
			return <Toggle isChecked={showToggle.isChecked} isDisabled={showToggle.isDisabled} />;
		}
		return null;
	};

	return (
		<DropdownItem
			elemBefore={icon ?? null}
			key={id}
			testId={testId}
			href={url}
			target={target}
			onClick={onClick}
			isDisabled={isDisabled}
			elemAfter={<ElementAfter />}
		>
			{text}
		</DropdownItem>
	);
};

export const CopyMenuItem = ({
	setIsCopyContentModalOpen,
	closeParentMenu,
	item,
	showIcon = true,
}: {
	setIsCopyContentModalOpen: any;
	closeParentMenu: any;
	item: any;
	showIcon?: boolean | undefined;
}): React.JSX.Element => (
	<DropdownItem
		key={ACTION_COPY_CONTENT}
		onClick={() => {
			closeParentMenu();
			setIsCopyContentModalOpen(true);
		}}
		elemBefore={showIcon ? <CopyIcon label="" /> : null}
	>
		{item.label}
	</DropdownItem>
);

export const NestedDatabaseCopyMenu = ({
	isNestedMenuOpen,
	handleClick,
	copyDatabaseItem,
	copyStructureItem,
	setIsCopyContentModalOpen,
	contentMode,
	handleNestedMenuItemClose,
	closeParentMenu,
}: {
	isNestedMenuOpen: any;
	handleClick: any;
	copyDatabaseItem: any;
	copyStructureItem: any;
	setIsCopyContentModalOpen: any;
	contentMode: any;
	handleNestedMenuItemClose: any;
	closeParentMenu: any;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const copyDatabaseLabel = formatMessage(
		expValEquals('make_a_copy_cta', 'cohort', 'test')
			? i18n.makeACopyOfDatabase
			: i18n.duplicateDatabase,
	);

	const copyOnClick = copyStructureItem.onClick;
	copyStructureItem.onClick = () => {
		closeParentMenu();
		copyOnClick?.();
	};
	if (contentMode === 'archived') {
		return (
			<CustomAction
				item={copyStructureItem}
				key={copyStructureItem.id}
				icon={<CopyIcon label="" />}
			/>
		);
	}

	const copyStructureItemWithComponent = {
		...copyStructureItem,
		customComponent: () => <CustomAction item={copyStructureItem} key={copyStructureItem.id} />,
	};

	const copyItems = [
		{
			...copyDatabaseItem,
			customComponent: () => (
				<CopyMenuItem
					setIsCopyContentModalOpen={setIsCopyContentModalOpen}
					closeParentMenu={closeParentMenu}
					item={{ ...copyDatabaseItem, label: copyDatabaseLabel }}
					showIcon={false}
				/>
			),
		},
		copyStructureItemWithComponent,
	];

	return (
		<ExpandableNestedMenuItem
			label={
				<FormattedMessage
					{...(expValEquals('make_a_copy_cta', 'cohort', 'test') ? i18n.makeACopy : i18n.duplicate)}
				/>
			}
			items={copyItems}
			isNestedMenuOpen={isNestedMenuOpen}
			onClick={handleClick}
			iconBefore={<CopyIcon label="" />}
			onMenuClose={handleNestedMenuItemClose}
		/>
	);
};

export const ViewChangesItem = ({
	contentId,
	dialogs,
	saveDraft,
	createAnalyticsEvent,
}: {
	contentId: any;
	dialogs: any;
	saveDraft: any;
	createAnalyticsEvent: any;
}): React.JSX.Element => {
	const intl = useIntl();

	const onClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'overflowViewChanges',
				source: 'overflowMenu',
			},
		}).fire();
		saveDraft?.();
		dialogs.showModal(ViewChangesDialogLoader, {
			contentId,
			title: intl.formatMessage(i18n.viewChangesTitle),
		});
	}, [contentId, dialogs, intl, saveDraft, createAnalyticsEvent]);

	return (
		<DropdownItem key="view-changes" onClick={onClick} elemBefore={<ShapesIcon label="" />}>
			<FormattedMessage {...i18n.viewChangesLabel} />
		</DropdownItem>
	);
};

export const ContentOwnershipActionItem = ({
	item,
	nestedPopup,
	setNestedPopup,
	contentId,
	setIsMenuOpen,
}: {
	item: any;
	nestedPopup: any;
	setNestedPopup: any;
	contentId: any;
	setIsMenuOpen: any;
}): React.JSX.Element => {
	const handleMenuClose = () => {
		setIsMenuOpen(false);
	};

	const handleOnClick = () => {
		if (nestedPopup === NestedMenuTypes.ContentOwnership) {
			setNestedPopup(undefined);
		} else {
			setNestedPopup(NestedMenuTypes.ContentOwnership);
		}
	};

	const handlePopupClose = () => {
		setNestedPopup(undefined);
	};

	return (
		<ContentOwnerPopup
			contentId={contentId}
			itemId={item.id}
			itemLabel={item.label}
			isOpen={nestedPopup === NestedMenuTypes.ContentOwnership}
			onClick={handleOnClick}
			onCloseParent={handleMenuClose}
			onClosePopup={handlePopupClose}
			elemBefore={fg('confluence_frontend_content_wrapper') ? <PersonIcon label="" /> : null}
		/>
	);
};

export const getAppsMenuItem = ({
	isAppsRendered,
	spaceKey,
	contentId,
	isMenuOpen,
	nestedPopup,
	items,
	handleNestedMenuItemClick,
	createAnalyticsEvent,
	handleNestedMenuItemClose,
	closeParentMenu,
}: {
	isAppsRendered: any;
	spaceKey: any;
	contentId: any;
	isMenuOpen: any;
	nestedPopup: any;
	items: any;
	handleNestedMenuItemClick: any;
	createAnalyticsEvent: any;
	handleNestedMenuItemClose: any;
	closeParentMenu: any;
}): {
	name: string;
	shouldRender: any;
	component: () => React.JSX.Element;
} => {
	const pageAppItems = items
		.filter(
			(item) =>
				item?.completeKey?.includes(CONNECT_APP_PREFIX_KEY) &&
				item?.id !== ANALYTICS_ADDON_ACTIONS_MENU_ID,
		)
		.map((item) => {
			item.closeParentMenu = closeParentMenu;
			return item;
		});

	const handleAppsMenuItemClick = () => {
		handleNestedMenuItemClick(NestedMenuTypes.Apps);
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'overflowApps',
				source: 'overflowMenu',
			},
		}).fire();
	};

	return {
		name: ACTION_APPS,
		shouldRender: isAppsRendered,
		component: (): React.JSX.Element => (
			<LazyForgeContentTools
				spaceKey={spaceKey}
				contentId={contentId}
				onLoadComplete={() => {}}
				isDropdownMenuOpen={isMenuOpen}
			>
				{(forgeMenuItems) =>
					pageAppItems.length > 0 || forgeMenuItems.length > 0 ? (
						<ExpandableNestedMenuItem
							label={<FormattedMessage {...i18n.pageAppsLabel} />}
							items={pageAppItems}
							isNestedMenuOpen={nestedPopup === NestedMenuTypes.Apps}
							onClick={handleAppsMenuItemClick}
							iconBefore={<AppsIcon label="" />}
							forgeMenuItems={forgeMenuItems}
							onMenuClose={handleNestedMenuItemClose}
							id="third-party"
						/>
					) : null
				}
			</LazyForgeContentTools>
		),
	};
};

export const AutomationsMenuItem = ({
	experienceTracker,
	contentId,
	spaceKey,
	pageTitle,
}: {
	experienceTracker: any;
	contentId: any;
	spaceKey: any;
	pageTitle: any;
}): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const intl = useIntl();

	return (
		<Subscribe to={[DialogsStateContainer]}>
			{(dialogs: DialogsStateContainer) => (
				<DropdownItem
					elemBefore={<AutomationIcon label="" />}
					onClick={() => {
						experienceTracker.start({
							name: AUTOMATION_MANUAL_TRIGGERS,
						});
						dialogs.showModal(PageQuickActionAutomationMenu, {
							pageId: contentId,
							pageTitle: pageTitle || intl.formatMessage(i18n.automationsModalTitle),
							spaceKey,
						});
						createAnalyticsEvent({
							type: 'sendUIEvent',
							data: {
								action: 'clicked',
								actionSubject: 'button',
								actionSubjectId: 'overflowAutomations',
								source: 'overflowMenu',
							},
						}).fire();
					}}
				>
					{intl.formatMessage(i18n.automationsLabel)}
				</DropdownItem>
			)}
		</Subscribe>
	);
};

export const AdvancedMenuItem = ({
	webItemsMap,
	handleNestedMenuItemClick,
	nestedPopup,
	handleNestedMenuItemClose,
	closeParentMenu,
}: {
	webItemsMap: any;
	handleNestedMenuItemClick: any;
	nestedPopup: any;
	handleNestedMenuItemClose: any;
	closeParentMenu: any;
}): React.JSX.Element | null => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const viewStorageFormatItem = webItemsMap[ACTION_VIEW_STORAGE_FORMAT];
	const viewSourceItem = fg('cc-page-experiences-enable-view-source-action-btn')
		? webItemsMap[ACTION_VIEW_SOURCE]
		: null;

	const advancedItems: ExpandableNestedMenuItemItemType[] = [];
	if (viewSourceItem) {
		advancedItems.push({ ...viewSourceItem, closeParentMenu });
	}
	if (viewStorageFormatItem) {
		advancedItems.push({ ...viewStorageFormatItem, closeParentMenu });
	}

	if (advancedItems.length === 0) {
		return null;
	}

	const onClick = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'overflowAdvanced',
				source: 'overflowMenu',
			},
		}).fire();
		handleNestedMenuItemClick(NestedMenuTypes.Advanced);
	};

	return (
		<ExpandableNestedMenuItem
			label={<FormattedMessage {...i18n.advancedLabel} />}
			items={advancedItems}
			iconBefore={<ShowMoreIcon label="" size="small" />}
			isNestedMenuOpen={nestedPopup === NestedMenuTypes.Advanced}
			onClick={onClick}
			key={ACTION_EXPORT_CONTENT}
			onMenuClose={handleNestedMenuItemClose}
		/>
	);
};
